<template>
  <div class="recovery-page" :class="{ 'dark-mode': isDark }" :dir="direction">
    <header class="recovery-header">
      <div class="recovery-brand">
        <span class="recovery-brand-icon"
          ><v-icon size="29">mdi-hospital-building</v-icon></span
        >
        <div>
          <strong>{{ copy.brand }}</strong>
          <small>{{ copy.brandSubtitle }}</small>
        </div>
      </div>
      <button type="button" class="recovery-language" @click="toggleLanguage">
        <v-icon size="18">mdi-translate</v-icon>
        {{ direction === "rtl" ? "العربية" : "English" }}
      </button>
    </header>

    <main class="recovery-main">
      <section class="recovery-card" aria-labelledby="recovery-title">
        <span class="recovery-emblem"
          ><v-icon size="32">mdi-email-lock-outline</v-icon></span
        >
        <h1 id="recovery-title">{{ copy.title }}</h1>
        <p class="recovery-intro">{{ copy.intro }}</p>

        <v-alert
          v-if="success"
          type="success"
          variant="tonal"
          class="recovery-alert"
          role="status"
        >
          {{ copy.success }}
        </v-alert>
        <v-alert
          v-if="errorMessage"
          type="error"
          variant="tonal"
          class="recovery-alert"
          role="alert"
        >
          {{ errorMessage }}
        </v-alert>

        <form class="recovery-form" @submit.prevent="submit">
          <label for="recovery-email">{{ copy.email }}</label>
          <v-text-field
            id="recovery-email"
            v-model="email"
            type="email"
            autocomplete="email"
            inputmode="email"
            prepend-inner-icon="mdi-email-outline"
            :placeholder="copy.emailPlaceholder"
            variant="outlined"
            density="comfortable"
            hide-details
            :disabled="loading"
          />
          <v-btn
            type="submit"
            class="recovery-submit"
            block
            height="48"
            :loading="loading"
            :disabled="loading"
          >
            {{ copy.submit }}
          </v-btn>
        </form>

        <router-link class="recovery-back" to="/login">
          <v-icon size="18">mdi-arrow-left</v-icon>
          {{ copy.back }}
        </router-link>
      </section>
    </main>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import api from "@/plugins/api";

const { locale } = useI18n();
const email = ref("");
const loading = ref(false);
const success = ref(false);
const errorMessage = ref("");
const isDark = ref(false);
const direction = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));
const copy = computed(() =>
  direction.value === "rtl"
    ? {
        brand: "نظام تقييم المرافق الصحية",
        brandSubtitle: "استعادة الوصول إلى الحساب",
        title: "نسيت كلمة المرور؟",
        intro:
          "أدخل بريدك الإلكتروني المسجّل وسنرسل لك رابطًا لتعيين كلمة مرور جديدة.",
        email: "البريد الإلكتروني",
        emailPlaceholder: "أدخل بريدك الإلكتروني",
        submit: "إرسال رابط الاستعادة",
        success:
          "إذا كان البريد مرتبطًا بحساب مفعّل، فستصلك رسالة الاستعادة قريبًا. تفقد البريد غير المرغوب فيه أيضًا.",
        back: "العودة إلى تسجيل الدخول",
        required: "يرجى إدخال بريد إلكتروني صحيح.",
        failure: "تعذر إرسال الطلب الآن. حاول مرة أخرى لاحقًا.",
      }
    : {
        brand: "Health Facility Assessment System",
        brandSubtitle: "Account recovery",
        title: "Forgot your password?",
        intro:
          "Enter your registered email and we will send you a link to set a new password.",
        email: "Email address",
        emailPlaceholder: "Enter your email address",
        submit: "Send reset link",
        success:
          "If this email belongs to an active account, a reset link will arrive shortly. Check your spam folder too.",
        back: "Back to sign in",
        required: "Enter a valid email address.",
        failure:
          "Could not send the request right now. Please try again later.",
      }
);

function toggleLanguage() {
  locale.value = direction.value === "rtl" ? "en" : "ar";
  localStorage.setItem("lang", locale.value);
  document.documentElement.lang = locale.value;
  document.documentElement.dir = direction.value;
  document.body.dir = direction.value;
}

async function submit() {
  errorMessage.value = "";
  success.value = false;
  const value = email.value.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    errorMessage.value = copy.value.required;
    return;
  }

  loading.value = true;
  try {
    await api.post("/forgot-password", { email: value });
    success.value = true;
  } catch {
    errorMessage.value = copy.value.failure;
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  const savedLanguage = localStorage.getItem("lang");
  if (savedLanguage === "ar" || savedLanguage === "en")
    locale.value = savedLanguage;
  isDark.value = localStorage.getItem("theme") === "dark";
  document.documentElement.lang = locale.value;
  document.documentElement.dir = direction.value;
  document.body.dir = direction.value;
});
</script>

<style scoped>
.recovery-page {
  --recovery-navy: #173f6b;
  --recovery-gold: #c49a3a;
  box-sizing: border-box;
  min-height: 100vh;
  background: linear-gradient(180deg, #eef3f8 0%, #f7f9fc 100%);
  color: #0f172a;
  font-family: "Cairo", "Inter", sans-serif;
}

.recovery-page *,
.recovery-page *::before,
.recovery-page *::after {
  box-sizing: border-box;
}
.recovery-page[dir="rtl"] {
  direction: rtl;
  text-align: right;
}
.recovery-page[dir="ltr"] {
  direction: ltr;
  text-align: left;
}

.recovery-header {
  min-height: 76px;
  padding: 12px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: #fff;
  background: var(--recovery-navy);
  border-bottom: 4px solid var(--recovery-gold);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14);
}

.recovery-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.recovery-brand-icon {
  display: grid;
  place-items: center;
  flex: 0 0 48px;
  height: 44px;
  border-radius: 11px;
  background: #fff;
  color: var(--recovery-navy);
}
.recovery-brand strong {
  display: block;
  overflow-wrap: anywhere;
  font-size: 17px;
  font-weight: 900;
  line-height: 1.5;
}
.recovery-brand small {
  display: block;
  color: #d9e6f3;
  font-size: 11px;
  line-height: 1.5;
}
.recovery-language {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 39px;
  padding: 7px 11px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 10px;
  color: #fff;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 800;
}
.recovery-language:hover {
  background: rgba(255, 255, 255, 0.12);
}

.recovery-main {
  min-height: calc(100vh - 76px);
  padding: 36px 16px 60px;
  display: grid;
  place-items: center;
}
.recovery-card {
  width: min(100%, 480px);
  padding: 30px;
  border: 1px solid #dce5ef;
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.1);
}
.recovery-emblem {
  width: 64px;
  height: 64px;
  display: grid;
  place-items: center;
  margin-bottom: 20px;
  border-radius: 17px;
  color: var(--recovery-navy);
  background: #edf3f9;
}
.recovery-card h1 {
  margin: 0 0 8px;
  font-size: 23px;
  line-height: 1.5;
  font-weight: 900;
}
.recovery-intro {
  margin: 0 0 24px;
  color: #57677b;
  font-size: 13px;
  line-height: 1.9;
}
.recovery-alert {
  margin-bottom: 18px;
  font-size: 12px;
  line-height: 1.7;
}
.recovery-form {
  display: grid;
  gap: 10px;
}
.recovery-form label {
  margin-top: 5px;
  font-size: 12px;
  font-weight: 800;
}
.recovery-form :deep(.v-field) {
  border-radius: 11px;
}
.recovery-submit {
  margin-top: 16px;
  border-radius: 11px !important;
  background: var(--recovery-navy) !important;
  color: #fff !important;
  font-weight: 800 !important;
  letter-spacing: 0 !important;
}
.recovery-submit:hover {
  background: #102d50 !important;
}
.recovery-back {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 23px;
  color: var(--recovery-navy);
  text-decoration: none;
  font-size: 12px;
  font-weight: 800;
}
.recovery-back:hover {
  text-decoration: underline;
}
.recovery-page[dir="rtl"] .recovery-back .v-icon {
  transform: rotate(180deg);
}

.recovery-page.dark-mode {
  color: #edf4fb;
  background: linear-gradient(180deg, #101c2d, #17273b);
}
.recovery-page.dark-mode .recovery-card {
  background: #1b2d42;
  border-color: #354b62;
}
.recovery-page.dark-mode .recovery-intro {
  color: #b7c8d9;
}
.recovery-page.dark-mode .recovery-emblem {
  color: #d7e8f8;
  background: #29435f;
}
.recovery-page.dark-mode .recovery-back {
  color: #bcd7f4;
}
.recovery-page.dark-mode .recovery-form :deep(.v-field) {
  color: #edf4fb;
}

@media (max-width: 600px) {
  .recovery-header {
    padding: 12px 16px;
  }
  .recovery-brand strong {
    font-size: 13px;
  }
  .recovery-brand small {
    font-size: 10px;
  }
  .recovery-brand-icon {
    flex-basis: 40px;
    height: 40px;
  }
  .recovery-language {
    padding: 6px 8px;
    font-size: 11px;
  }
  .recovery-main {
    padding: 22px 12px 40px;
    place-items: start center;
  }
  .recovery-card {
    padding: 22px 18px;
    border-radius: 17px;
  }
  .recovery-card h1 {
    font-size: 20px;
  }
}
</style>
